import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { Maybe } from "graphql/jsutils/Maybe";

import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { FormType, ListEntry } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { InputAnimalOrigin } from "../../../../../../graphql/generated/blonk/pigs";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { ShrimpBaseline, ShrimpGrowingStageData } from "../../../../models/Baseline/ShrimpBaseline";
import { formatLabelName } from "../../../../utils/string-utils";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";

interface ShrimpChemicalFormProps {
  stageIndex?: number | undefined;
  itemIndex?: number | undefined;
  formType: FormType;
  editEnabled?: boolean;
  required?: boolean;
  options: ListEntry[];
  baseline: ShrimpBaseline;
}

const ShrimpChemicalForm: FC<ShrimpChemicalFormProps> = ({
  stageIndex = 0,
  itemIndex = 0,
  formType,
  required = false,
  editEnabled = false,
  baseline,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const barnOutputMassUnit = userUOM?.unitBarnOutputMass
    ? unitLong(userUOM.unitBarnOutputMass)
    : "kg";

  const formContext = useFormContext();
  const propName = "chemicalTypes";

  const fieldItemPrefix = `stages.${stageIndex}.stageData.operations.${propName}.${itemIndex}`;
  const chemicalsData =
    (baseline.stages[stageIndex].stageData as ShrimpGrowingStageData).operations?.chemicalTypes[itemIndex];

  return (
    <>
      <input
        ref={formContext.register()}
        type="hidden"
        name={`${fieldItemPrefix}.id`}
        value={chemicalsData?.id}
      />
      <input
        ref={formContext.register()}
        type="hidden"
        name={`${fieldItemPrefix}.type`}
        value={chemicalsData?.type}
      />
      <DsmGrid className={classes.dsmGridTwoColumn}>
        {!editEnabled && (formContext.getValues(`${fieldItemPrefix}.amount`) === undefined ||
          (formContext.getValues(`${fieldItemPrefix}.amount`) === "")) ? (
          <ReactHookDsmInput
            name={`${fieldItemPrefix}.originalAmount`}
            label={formatLabelName(chemicalsData?.type as unknown as string)}
            tooltip={intl.formatMessage({
              id: "BASELINE.FORM.OPERATIONS.SHRIMP.CHEMICAL_TYPE_AMOUNT.TOOLTIP",
            })}
            adornment={barnOutputMassUnit}
            type="number"
            disabled
            required={required}
            defaultValue={chemicalsData?.amount}
          />
        ) : (
          <>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.amount`}
              label={formatLabelName(chemicalsData?.type as unknown as string)}
              type="number"
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.OPERATIONS.SHRIMP.CHEMICAL_TYPE_AMOUNT.TOOLTIP",
              })}
              metricUnit={barnOutputMassUnit}
              baseline={chemicalsData?.amount}
              disabled={formType === FormType.View || !editEnabled}
              displayTypeSelect={editEnabled}
            />
          </>
        )}
      </DsmGrid>
    </>
  );
};

export default ShrimpChemicalForm;
